import { FabricImage, Shadow } from 'fabric';
import { FabricJSCanvas, useFabricJSEditor } from 'fabricjs-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrandCategory, BRANDS_ALPHANUMERIC, BRANDS_GREEK_ALPHABET, 
  BRANDS_SHAPES_SYMBOLS } from '../constants/brands';
import { disableBrandScaling } from '../constants/brandControls';
import { getBrandAssetFolderForModel } from '../constants/brandAssets';
import { getBrandPickerPreviewScale, getBrandScalesForModel } from '../constants/brandScale';
import {
  getBrandEntryScaleMultiplier,
  getBrandExitScaleMultiplier,
  MOTION_DURATION_MS,
  runMotion,
} from '../constants/motion';
import { getModelSideImageUrl } from '../constants/publicAssetUrls';
import '../App.css';

function CanvasWrapper(props) {
  const { editor, onReady } = useFabricJSEditor();
  const [selectedCategory, setSelectedCategory] = useState(BrandCategory.SHAPES_SYMBOLS);
  const [activeObject, setActiveObject] = useState(null);
  const [isBrandGridRefreshing, setIsBrandGridRefreshing] = useState(false);
  const [canvasDisplayScale, setCanvasDisplayScale] = useState(1);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const selectionMotionCancel = useRef(null);
  const selectionGlowObjects = useRef([]);
  const hydratedDesignKey = useRef(null);
  const brandCanvasViewportRef = useRef(null);
  const canvasBackgroundStyle = props.sideImagePath
    ? { '--canvas-bg-image': `url("${getModelSideImageUrl(props.sideImagePath)}")` }
    : undefined;
  const isWideSide = props.side === "front" || props.side === "back" || props.side === "top";
  const logicalCanvasWidth = isWideSide ? 600 : 300;
  const logicalCanvasHeight = 300;
  const brandPlacementLeft = isWideSide ? 300 : 150;
  const getSelectedObjects = () => editor?.canvas?.getActiveObjects?.() ?? [];
  const toDataUrl = (url, callback) => {
    var xhr = new XMLHttpRequest();
    xhr.onload = function() {
        var reader = new FileReader();
        reader.onloadend = function() {
            callback(reader.result);
        }
        reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  }

  const getBrandAssetName = (brand) => {
    return brand;
  };

  const getBrandAssetFolder = (category = selectedCategory) => {
    return getBrandAssetFolderForModel(props.selectedModel, category);
  };

  const brandAssetFolder = getBrandAssetFolder();

  const addImage = async (
    brand,
    left,
    right,
    scaleX,
    angle,
    shouldAnimate = true,
    category = selectedCategory,
    scaleY = scaleX
  ) => {
    const assetName = getBrandAssetName(brand);
    const url = '/' + getBrandAssetFolder(category) + '/' + assetName + '.png';
    toDataUrl(url, async function(base64) {
      const img = await FabricImage.fromURL(base64);
      const brandScales = scaleX == null
        ? getBrandScalesForModel({
            imageWidth: img.width,
            imageHeight: img.height,
            model: props.selectedModel,
            selectedCategory: category,
            side: props.side,
            assetName: assetName,
          })
        : { scaleX, scaleY: scaleY ?? scaleX };
      const canvas = editor?.canvas;
      if (!canvas) {
        return;
      }

      img.left = left;
      img.top = right;
      img.angle = angle;
      img.scaleX = shouldAnimate
        ? brandScales.scaleX * getBrandEntryScaleMultiplier(0)
        : brandScales.scaleX;
      img.scaleY = shouldAnimate
        ? brandScales.scaleY * getBrandEntryScaleMultiplier(0)
        : brandScales.scaleY;
      img.opacity = shouldAnimate ? 0 : 1;
      img.objectCaching = false;
      disableBrandScaling(img);
      img.centeredRotation = false;
      img.originX = 0.5;
      img.originY = 0.5;
      img.name = assetName;
      img.brandCategory = category;
      canvas.add(img);
      props.setRef(canvas);

      if (!shouldAnimate) {
        canvas.requestRenderAll();
        return;
      }

      props.onBrandInteractionChange?.(true);
      runMotion({
        duration: MOTION_DURATION_MS.deliberate,
        onFrame: (progress, elapsedProgress) => {
          const animatedScale = getBrandEntryScaleMultiplier(elapsedProgress);
          img.set({
            opacity: Math.min(progress * 1.12, 1),
            scaleX: brandScales.scaleX * animatedScale,
            scaleY: brandScales.scaleY * animatedScale,
          });
          img.setCoords();
          canvas.requestRenderAll();
        },
        onComplete: () => {
          props.onBrandInteractionChange?.(false);
          props.setRef(canvas);
        },
      });
    });
  }

  const removeSelectedBrand = () => {
    if (!editor?.canvas) {
      return;
    }

    const selectedObjects = editor.canvas.getActiveObjects();
    if (!selectedObjects.length) {
      return;
    }

    const canvas = editor.canvas;
    const startingTransforms = selectedObjects.map((selectedObject) => ({
      selectedObject,
      opacity: selectedObject.opacity ?? 1,
      scaleX: selectedObject.scaleX,
      scaleY: selectedObject.scaleY,
    }));

    canvas.discardActiveObject();
    setActiveObject(null);
    props.onBrandInteractionChange?.(true);

    runMotion({
      duration: MOTION_DURATION_MS.deliberate,
      onFrame: (_progress, elapsedProgress) => {
        const exitProgress = elapsedProgress * elapsedProgress;
        startingTransforms.forEach(({ selectedObject, opacity, scaleX, scaleY }) => {
          const scaleMultiplier = getBrandExitScaleMultiplier(exitProgress);
          selectedObject.set({
            evented: false,
            opacity: opacity * (1 - exitProgress),
            scaleX: scaleX * scaleMultiplier,
            scaleY: scaleY * scaleMultiplier,
            selectable: false,
          });
          selectedObject.setCoords();
        });
        canvas.requestRenderAll();
      },
      onComplete: () => {
        startingTransforms.forEach(({ selectedObject }) => canvas.remove(selectedObject));
        canvas.requestRenderAll();
        props.onBrandInteractionChange?.(false);
        props.setRef(canvas);
      },
    });
  };

  const handleCategoryChange = (event) => {
    setIsBrandGridRefreshing(true);
    setSelectedCategory(event.target.value);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setIsBrandGridRefreshing(false));
    });
  };

  const getCurrentBrands = () => {
    if (selectedCategory === BrandCategory.SHAPES_SYMBOLS) {
      return BRANDS_SHAPES_SYMBOLS;
    } else if (selectedCategory === BrandCategory.ALPHANUMERIC_LARGE) {
      return BRANDS_ALPHANUMERIC;
    } else if (selectedCategory === BrandCategory.ALPHANUMERIC_SMALL) {
      return BRANDS_ALPHANUMERIC;
    } else if (selectedCategory === BrandCategory.GREEK_ALPHABET) {
      return BRANDS_GREEK_ALPHABET;
    }

    return BRANDS_SHAPES_SYMBOLS;
  }

  useLayoutEffect(() => {
    const updateDisplayScale = () => {
      const availableWidth = brandCanvasViewportRef.current?.clientWidth ?? logicalCanvasWidth;
      setCanvasDisplayScale(Math.min(1, availableWidth / logicalCanvasWidth));
    };

    updateDisplayScale();
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateDisplayScale);
    if (brandCanvasViewportRef.current) {
      resizeObserver?.observe(brandCanvasViewportRef.current);
    }
    window.addEventListener('resize', updateDisplayScale);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateDisplayScale);
    };
  }, [logicalCanvasWidth]);

  useEffect(() => {
    const canvas = editor?.canvas;
    const viewport = brandCanvasViewportRef.current;
    if (!canvas || !viewport) {
      return;
    }

    canvas.selection = canvasZoom <= 1;

    let panStart = null;
    const getPointer = (event) => {
      const sourceEvent = event?.e;
      if (sourceEvent?.touches?.length > 1) {
        return null;
      }
      const point = sourceEvent?.touches?.[0] ?? sourceEvent?.changedTouches?.[0] ?? sourceEvent;
      return point && Number.isFinite(point.clientX)
        ? { x: point.clientX, y: point.clientY }
        : null;
    };

    const handlePanStart = (event) => {
      if (canvasZoom <= 1 || event.target) {
        return;
      }
      const point = getPointer(event);
      if (!point) {
        return;
      }
      panStart = {
        x: point.x,
        y: point.y,
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop,
      };
      canvas.defaultCursor = 'grabbing';
    };

    const handlePanMove = (event) => {
      if (!panStart) {
        return;
      }
      const point = getPointer(event);
      if (!point) {
        return;
      }
      event.e?.preventDefault?.();
      viewport.scrollLeft = panStart.scrollLeft - (point.x - panStart.x);
      viewport.scrollTop = panStart.scrollTop - (point.y - panStart.y);
    };

    const handlePanEnd = () => {
      panStart = null;
      canvas.defaultCursor = canvasZoom > 1 ? 'grab' : 'default';
    };

    canvas.defaultCursor = canvasZoom > 1 ? 'grab' : 'default';
    canvas.on('mouse:down', handlePanStart);
    canvas.on('mouse:move', handlePanMove);
    canvas.on('mouse:up', handlePanEnd);

    return () => {
      canvas.off('mouse:down', handlePanStart);
      canvas.off('mouse:move', handlePanMove);
      canvas.off('mouse:up', handlePanEnd);
    };
  }, [editor, canvasZoom]);

  useEffect(() => {
    if (!editor?.canvas) {
      return;
    }

    editor.canvas.selection = true;
    editor.canvas.selectionKey = 'shiftKey';

    const clearSelectionGlow = () => {
      selectionGlowObjects.current.forEach((object) => object.set({ shadow: null }));
      selectionGlowObjects.current = [];
    };

    const handleSelectionChange = () => {
      const selectedObject = editor.canvas.getActiveObject() ?? null;
      selectionMotionCancel.current?.();
      clearSelectionGlow();
      disableBrandScaling(selectedObject);
      selectedObject?.set({
        borderColor: 'rgba(137, 104, 67, 0)',
        cornerColor: '#fffaf4',
        cornerStrokeColor: '#896843',
        cornerStyle: 'circle',
        transparentCorners: false,
      });
      selectedObject?.setCoords?.();
      editor.canvas.requestRenderAll();
      setActiveObject(selectedObject);

      if (selectedObject) {
        selectionGlowObjects.current = selectedObject.getObjects?.() ?? [selectedObject];
        selectionMotionCancel.current = runMotion({
          duration: MOTION_DURATION_MS.standard,
          onFrame: (progress) => {
            if (editor.canvas.getActiveObject() !== selectedObject) {
              return;
            }
            selectedObject.set({
              borderColor: `rgba(137, 104, 67, ${progress * 0.85})`,
            });
            selectionGlowObjects.current.forEach((object) => {
              object.set({
                shadow: new Shadow({
                  blur: 2 + progress * 10,
                  color: `rgba(255, 250, 244, ${progress * 0.92})`,
                  offsetX: 0,
                  offsetY: 0,
                }),
              });
            });
            editor.canvas.requestRenderAll();
          },
        });
      }
    };

    editor.canvas.on('selection:created', handleSelectionChange);
    editor.canvas.on('selection:updated', handleSelectionChange);
    editor.canvas.on('selection:cleared', handleSelectionChange);

    return () => {
      selectionMotionCancel.current?.();
      clearSelectionGlow();
      editor.canvas.off('selection:created', handleSelectionChange);
      editor.canvas.off('selection:updated', handleSelectionChange);
      editor.canvas.off('selection:cleared', handleSelectionChange);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor?.canvas) {
      return;
    }

    const handleObjectTransformStart = () => {
      props.onBrandInteractionChange?.(true);
    };

    const handleObjectTransformEnd = () => {
      props.onBrandInteractionChange?.(false);
      props.setRef(editor.canvas);
    };

    editor.canvas.on('object:moving', handleObjectTransformStart);
    editor.canvas.on('object:scaling', handleObjectTransformStart);
    editor.canvas.on('object:rotating', handleObjectTransformStart);
    editor.canvas.on('object:modified', handleObjectTransformEnd);
    editor.canvas.on('mouse:up', handleObjectTransformEnd);

    return () => {
      editor.canvas.off('object:moving', handleObjectTransformStart);
      editor.canvas.off('object:scaling', handleObjectTransformStart);
      editor.canvas.off('object:rotating', handleObjectTransformStart);
      editor.canvas.off('object:modified', handleObjectTransformEnd);
      editor.canvas.off('mouse:up', handleObjectTransformEnd);
    };
  }, [editor, props]);

  useEffect(() => {
    if (!editor?.canvas) {
      return;
    }

    const isTypingTarget = (target) =>
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable);

    const handleKeyDown = (event) => {
      const selectedObject = editor.canvas.getActiveObject();
      if (!selectedObject) {
        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        removeSelectedBrand();
        return;
      }

      const nudgeDistance = event.shiftKey ? 10 : 1;
      let deltaX = 0;
      let deltaY = 0;

      if (event.key === 'ArrowLeft') {
        deltaX = -nudgeDistance;
      } else if (event.key === 'ArrowRight') {
        deltaX = nudgeDistance;
      } else if (event.key === 'ArrowUp') {
        deltaY = -nudgeDistance;
      } else if (event.key === 'ArrowDown') {
        deltaY = nudgeDistance;
      } else {
        return;
      }

      event.preventDefault();
      selectedObject.set({
        left: selectedObject.left + deltaX,
        top: selectedObject.top + deltaY,
      });
      selectedObject.setCoords();
      editor.canvas.requestRenderAll();
      props.setRef(editor.canvas);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor?.canvas) {
      return;
    }

    const initialEntries = props.initialDesignEntries ?? [];
    const hydrationKey = JSON.stringify(initialEntries);
    if (hydratedDesignKey.current === hydrationKey) {
      return;
    }
    hydratedDesignKey.current = hydrationKey;

    if (initialEntries.length) {
      initialEntries.forEach((entry) => {
        addImage(
          entry.assetName,
          entry.left,
          entry.top,
          entry.scaleX,
          entry.angle,
          false,
          entry.category,
          entry.scaleY
        );
      });
      return;
    }

    // Preserve compatibility with links generated by the original Shopify
    // integration while new orders use the versioned `design` token.
    const url = new URL(document.location.href);
    const sideParam = url.searchParams.get(props.side);
    if (!sideParam) {
      return;
    }

    sideParam.split("~").forEach((sideEntry) => {
      const imageSplit = sideEntry.split("|");
      if (imageSplit.length === 5) {
        addImage(
          imageSplit[0],
          parseFloat(imageSplit[1]),
          parseFloat(imageSplit[2]),
          parseFloat(imageSplit[3]),
          parseFloat(imageSplit[4]),
          false
        );
      }
    });
  }, [editor, props.initialDesignEntries, props.side]);

  return (
    <div className="workflow-side-content">
      <section className="workflow-section" aria-labelledby={`add-brand-${props.side}`}>
        <h3 id={`add-brand-${props.side}`} className="workflow-heading"><span className="workflow-step">02</span> Add Your Brands</h3>
        <p className="workflow-description">Click a brand below to add it to your bag</p>
        <div className="brand-category-control">
          <select
            className="studio-select"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            <option value={BrandCategory.SHAPES_SYMBOLS}>Shapes and Symbols</option>
            <option value={BrandCategory.ALPHANUMERIC_LARGE}>1" Letters and Numbers</option>
            <option value={BrandCategory.ALPHANUMERIC_SMALL}>1/2" Letters and Numbers</option>
            <option value={BrandCategory.GREEK_ALPHABET}>Greek Alphabet</option>
          </select>
        </div>

        <div className={`brand-picker__grid${isBrandGridRefreshing ? ' is-refreshing' : ''}`}>
          {getCurrentBrands().map((brand) => (
            <React.Fragment key={brand}>
              {(brand === 'everwest-small') ? <br /> : null}
              <button
                type="button"
                onClick={() => addImage(brand, brandPlacementLeft, 150, null, 0)}
                aria-label={`Add ${brand}`}
                className="brand-picker__button"
              >
                <img
                  src={`/${brandAssetFolder}/${getBrandAssetName(brand)}.png`}
                  alt=""
                  style={{
                    width: `${getBrandPickerPreviewScale(brand) * 100}%`,
                    height: `${getBrandPickerPreviewScale(brand) * 100}%`,
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </button>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="workflow-section" aria-labelledby={`arrange-brands-${props.side}`}>
        <h3 id={`arrange-brands-${props.side}`} className="workflow-heading"><span className="workflow-step">03</span> Arrange Your Brands</h3>
        <p className="workflow-description">Drag, rotate, and position your brands</p>
        <div
          className="brand-canvas-frame"
          style={{
            width: `${logicalCanvasWidth}px`,
            maxWidth: '100%',
          }}
        >
          <div
            ref={brandCanvasViewportRef}
            className="brand-canvas-viewport"
            style={{
              width: `${logicalCanvasWidth}px`,
              maxWidth: '100%',
              height: `${logicalCanvasHeight * canvasDisplayScale}px`,
            }}
          >
            <div
              className="brand-canvas-zoom-surface"
              style={{
                width: `${logicalCanvasWidth * canvasDisplayScale * canvasZoom}px`,
                height: `${logicalCanvasHeight * canvasDisplayScale * canvasZoom}px`,
              }}
            >
              <div
                className="brand-canvas"
                style={{
                  ...canvasBackgroundStyle,
                  width: `${logicalCanvasWidth}px`,
                  height: `${logicalCanvasHeight}px`,
                  transform: `scale(${canvasDisplayScale * canvasZoom})`,
                }}
              >
                <FabricJSCanvas
                  className={props.className}
                  onReady={(canvas) => {
                    canvas.setDimensions({
                      width: logicalCanvasWidth,
                      height: logicalCanvasHeight,
                    });
                    onReady(canvas);
                    canvas.enableRetinaScaling = true;
                    [canvas.contextContainer, canvas.contextTop].forEach((context) => {
                      if (context) {
                        context.imageSmoothingEnabled = true;
                        context.imageSmoothingQuality = 'high';
                      }
                    });
                    canvas.calcOffset();
                    props.setRef(canvas);
                  }}
                  style={{ ...canvasBackgroundStyle }}
                />
              </div>
            </div>
          </div>
          {activeObject ?
          <button
            className="studio-control remove-brand-button"
            onClick={removeSelectedBrand}
            aria-label={getSelectedObjects().length > 1 ? 'Remove selected brands' : 'Remove selected brand'}
          >
            Remove
          </button> : null }
        </div>
        <div className="brand-canvas-zoom-controls" aria-label="2D design zoom controls">
          <button
            type="button"
            className="studio-control brand-canvas-zoom-button"
            onClick={() => setCanvasZoom((zoom) => Math.max(1, zoom - 0.25))}
            disabled={canvasZoom <= 1}
            aria-label="Zoom out of 2D design"
          >
            −
          </button>
          <button
            type="button"
            className="studio-control brand-canvas-zoom-button"
            onClick={() => setCanvasZoom((zoom) => Math.min(2, zoom + 0.25))}
            disabled={canvasZoom >= 2}
            aria-label="Zoom in on 2D design"
          >
            +
          </button>
        </div>
      </section>
    </div>
  )
}

export default CanvasWrapper;
